<template>
  <FullScreenLoader v-show="rcCampaignCreationInProgress" />
  <BaseCard no-padding class="tw:lg:flex-1 tw:lg:flex tw:lg:flex-col">
    <div class="tw:relative tw:bg-white tw:w-full tw:rounded-xl tw:lg:flex-1 tw:lg:flex tw:lg:flex-col">
      <SetupStepper :steps="steps" />

      <div class="tw:lg:flex-1 tw:lg:flex tw:lg:flex-col">
        <CommissionSelection v-if="currentStep === 2" />
        <PaymentPage
          v-if="currentStep === 3"
          ref="billingPaymentStepRef"
          :isLoading="isLoading"
          :isExistingPaymentMethodsLoading="isExistingPaymentMethodsLoading"
          :isAvailablePaymentMethodsLoading="isAvailablePaymentMethodsLoading"
          :isError="isError"
          :zuoraAccountData="zuoraAccountData"
          :existingPaymentMethods="existingPaymentMethods"
          :availablePaymentMethodsData="availablePaymentMethodsData"
          :paypalConfig="paypalConfig"
          :currencyCode="currency"
          :selectedAccommodationId="selectedAccommodationId"
          :termsAccepted="termsAccepted"
          :selectedPaymentMethod="selectedPaymentMethod"
          :paymentEnabled="paymentEnabled"
          :existingBillingInformation="existingBillingInformation"
          :errorInfo="errorInfo"
          :isFetching="isFetching"
          :selectedCommissionLevel="selectedCommissionLevel"
          :language="language"
          :rcCampaignCreationInProgress="rcCampaignCreationInProgress"
          :isFromMarketingLink="isFromMarketingLink"
          :rcVoucher="rcVoucher"
          :isVoucherFetching="isVoucherFetching"
          :appliedCode="appliedCode"
          @applyVoucherCode="onApplyVoucherCode"
          @update:termsAccepted="termsAccepted = $event"
          @update:selectedPaymentMethod="selectedPaymentMethod = $event"
          @onShowNewFormCancel="onNewPaymentMethodFormCancel"
          @onZuoraCallbackSuccess="onZuoraCallbackSuccess"
          @onZuoraCallbackError="onZuoraCallbackError"
          @onZuoraClientValidationError="onZuoraClientValidationError"
          @onValidateAllSuccess="onValidateBillingSuccess"
          @onValidateAllError="onValidateBillingError"
          @setBillingFormValid="setBillingFormValid"
          @onStartCampaign="onStartCampaign"
        />
      </div>
      <SetupFooter
        @onNext="onNextAction"
        @onBack="onBackAction"
        @onStartCampaign="onStartCampaign"
        :paymentEnabled="paymentEnabled"
        :rcCampaignCreationInProgress="rcCampaignCreationInProgress"
      />
    </div>
  </BaseCard>
  <div class="tw:flex tw:flex-col tw:mt-12 tw:text-sm tw:text-center tw:gap-2 tw:px-4" v-if="currentStep === 2">
    <p>{{ $t('rc_pay_per_stay_billing_footnote') }}</p>
    <p>{{ $t('rc_commission_page_disclaimer_2') }}</p>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, nextTick, watch, onBeforeUnmount } from 'vue'
import { getRcSetupSteps } from '@/constants/rcSetupSteps'
import { useRouter } from 'vue-router'

import BaseCard from '@/components/dashboard/BaseCard.vue'
import SetupStepper from '@/components/rateConnect/setup/SetupStepper.vue'
import CommissionSelection from '@/components/rateConnect/setup/CommissionSelection.vue'
import PaymentPage from '@/components/rateConnect/setup/PaymentPage.vue'
import SetupFooter from '@/components/rateConnect/setup/SetupFooter.vue'

import { useSetup } from '@/components/rateConnect/setup/useSetup'
import { useRCVoucher } from '@/components/rateConnect/setup/useRCVoucher'
import {
  useAvailablePaymentMethods,
  useExistingPaymentMethods,
  useZuoraAccount,
  useZuoraUpdateAccountMutation,
  useZuoraNewAccountMutation,
  useCreateRateConnectCampaign,
  useErrorMetrics,
  useValidateRCVoucher,
} from '@/components/payment/queries'

import { selectedAccommodationId, useAccommodationsByOrgId, useUserDetails } from '@/layouts/queries'
import { countries } from '@/constants/countries.ts'
import { useSessionValues } from '@/composables/useSessionValues'
import { useSelectedPaymentMethodType } from '@/composables/useSelectedPaymentMethodType'
import { showPlusUpsellPopup } from '@/composables/useRcPlusCrossSell'

import useToastNotifications from '@/components/notifications/useToastNotifications'
import usePaymentNotifications from '@/components/notifications/usePaymentNotifications'
import zuoraMetricConstants from '@/constants/zuoraMetricConstants'
import appAnalytics from '@/utils/tracking'
import FullScreenLoader from '@/components/base/loaders/FullScreenLoader.vue'

const router = useRouter()
const { displayNotification } = useToastNotifications()
const { clearAllNotifications } = usePaymentNotifications()
const { currentStep, setCurrentStep, selectedCommissionLevel, selectedPartner, resetSetup } = useSetup()
const steps = computed(() => getRcSetupSteps(currentStep.value))

const onNextAction = () => {
  if (currentStep.value === 2) {
    setCurrentStep(3)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

const onBackAction = () => {
  if (currentStep.value === 2) {
    router.push({ name: 'rate-connect-no-campaign' })
  }
  if (currentStep.value === 3) {
    setCurrentStep(2)
  }
}

const { sendErrorMetrics } = useErrorMetrics()
const billingPaymentStepRef = ref(null)
const termsAccepted = ref(false)
const selectedPaymentMethod = ref(null)
const isBillingFormValid = ref(true)
const newBillingInformation = ref(null)
const isZuoraHppLoadError = ref(false)
const errorInfo = ref(false)
const countriesWithState = ['US', 'MX', 'CA', 'BR']
const rcCampaignCreationInProgress = ref(false)

const { language, selectedOrganisationId, currency, userRole, userId } = useSessionValues()
const { currentAccommodation } = useAccommodationsByOrgId(selectedOrganisationId)
const { userDetails } = useUserDetails(userId)

const { isLoading, isError, zuoraAccountData } = useZuoraAccount()
const { existingPaymentMethods, isLoading: isExistingPaymentMethodsLoading } =
  useExistingPaymentMethods(selectedAccommodationId)
const {
  isFetching,
  isLoading: isAvailablePaymentMethodsLoading,
  availablePaymentMethodsData,
  paypalConfig,
  refetch,
} = useAvailablePaymentMethods(selectedAccommodationId, currency)
const { updateZuoraAccount } = useZuoraUpdateAccountMutation()
const { createNewZuoraAccount } = useZuoraNewAccountMutation()
const { createNewRateConnectCampaign } = useCreateRateConnectCampaign()

const { appliedCode, isFromMarketingLink, clearPendingVoucher } = useRCVoucher()
const { rcVoucher, isFetching: isVoucherFetching } = useValidateRCVoucher({
  accommodationId: selectedAccommodationId,
  voucherCode: appliedCode,
  currencyCode: currency,
})

const onApplyVoucherCode = code => {
  appliedCode.value = code
}

const paymentEnabled = computed(() => {
  const selectedMethod = existingPaymentMethods.value?.find(item => item.id === selectedPaymentMethod.value)
  const isWireTransfer = selectedMethod?.type === 'WIRE_TRANSFER'

  return termsAccepted.value && selectedPaymentMethod.value !== null && isBillingFormValid.value && !isWireTransfer
})

const existingBillingInformation = computed(() => {
  if (
    !zuoraAccountData.value ||
    zuoraAccountData.value?.basicInfo === null ||
    zuoraAccountData.value?.billToContact === null
  )
    return null
  const countryCode = countries.find(country => country.name === zuoraAccountData.value?.billToContact?.country)?.code
  return {
    companyName: zuoraAccountData.value.basicInfo.name,
    email: zuoraAccountData.value.billToContact.workEmail,
    firstName: zuoraAccountData.value.billToContact.firstName,
    lastName: zuoraAccountData.value.billToContact.lastName,
    address: zuoraAccountData.value.billToContact.address1,
    city: zuoraAccountData.value.billToContact.city,
    zipCode: zuoraAccountData.value.billToContact.zipCode,
    country: countryCode,
    state: zuoraAccountData.value.billToContact.state,
    taxId: zuoraAccountData.value.taxInfo?.exemptCertificateId,
    noTaxId: zuoraAccountData.value.taxInfo?.exemptCertificateId ? false : true,
  }
})

const onNewPaymentMethodFormCancel = () => {
  errorInfo.value = false
  if (isSelectedPaymentMethodNew.value) {
    selectedPaymentMethod.value = defaultPaymentMethodId.value
  }
}

const defaultPaymentMethodId = computed(
  () => existingPaymentMethods.value?.find(paymentMethod => paymentMethod.isDefault)?.id
)

const isSelectedPaymentMethodNew = computed(
  () => selectedPaymentMethod.value === 'CREDIT_CARD' || selectedPaymentMethod.value === 'SEPA'
)

const selectedPaymentMethodType = useSelectedPaymentMethodType(selectedPaymentMethod, existingPaymentMethods)

const setBillingFormValid = isValid => {
  isBillingFormValid.value = isValid
}

const onValidateBillingError = () => {
  displayNotification({
    message: 'subscriptions.details.transactionHistory.error',
    isTranslationKey: true,
    type: 'error',
  })
  rcCampaignCreationInProgress.value = false
}

const onStartCampaign = () => {
  if (isVoucherFetching.value) return
  if (isSelectedPaymentMethodNew.value && isZuoraHppLoadError.value) {
    displayNotification({
      message: 'subscriptions.details.transactionHistory.error',
      isTranslationKey: true,
      type: 'error',
    })
    return
  }
  rcCampaignCreationInProgress.value = true
  billingPaymentStepRef.value?.billingForm?.validateAll()
}

const onValidateBillingSuccess = billingInfo => {
  newBillingInformation.value = billingInfo
  if (!selectedPaymentMethod.value) {
    displayNotification({
      message: 'subscriptions.details.transactionHistory.error',
      isTranslationKey: true,
      type: 'error',
    })
    rcCampaignCreationInProgress.value = false

    return
  }
  if (isSelectedPaymentMethodNew.value) {
    if (userRole.value === 'support') {
      rcCampaignCreationInProgress.value = false
      return
    }
    Z.submit()
    return
  }
  if (selectedPaymentMethod.value !== defaultPaymentMethodId.value) {
    sendZuoraAccount(selectedPaymentMethod.value)
    return
  }
  sendZuoraAccount()
}

const onZuoraCallbackSuccess = async refId => {
  sendZuoraAccount(refId)
}

const forceRender = async () => {
  await nextTick()
  refetch()
  isZuoraHppLoadError.value = false
}

const onZuoraClientValidationError = (_, isAttemptExceedLimitationError) => {
  rcCampaignCreationInProgress.value = false
  if (isAttemptExceedLimitationError) forceRender()
}

const onZuoraCallbackError = (widthRerender = false) => {
  if (rcCampaignCreationInProgress.value) {
    displayNotification({
      message: 'subscriptions.details.transactionHistory.error',
      isTranslationKey: true,
      type: 'error',
    })
  } else {
    if (widthRerender) {
      forceRender()
    }
    isZuoraHppLoadError.value = true
  }
  rcCampaignCreationInProgress.value = false
}

const createRateConnectSubscription = async () => {
  try {
    await createNewRateConnectCampaign({
      accommodationId: selectedAccommodationId.value,
      body: {
        itemName: currentAccommodation.value.name,
        campaignType: 'netCpa',
        currencyCode: currency?.value,
        partnerId: selectedPartner.value?.partnerId,
        cpa: selectedCommissionLevel.value,
        language: language?.value,
        lastName: userDetails.value?.lastName,
        firstName: userDetails.value?.firstName,
        ...(rcVoucher.value?.isValid && { voucherCode: rcVoucher.value.voucherCode }),
      },
    })
    clearPendingVoucher()

    appAnalytics.track(appAnalytics.events.RC_ONBOARDING_PAYMENT_SUCCESS, {
      item_id: selectedAccommodationId.value,
      partner_id: selectedPartner.value?.partnerId,
      commission_level: selectedCommissionLevel.value,
    })

    showPlusUpsellPopup()

    await router.push({ name: 'rate-connect-rate-check' })
  } catch {
    resetSetup()
    displayNotification({
      message: 'app_error_title',
      isTranslationKey: true,
      type: 'error',
    })
  } finally {
    rcCampaignCreationInProgress.value = false
  }
}

const sendZuoraAccount = async refId => {
  rcCampaignCreationInProgress.value = true
  if (!existingBillingInformation.value) {
    const body = {
      name: newBillingInformation.value?.companyName,
      organization_id__c: selectedOrganisationId.value,
      thm_account_type__c: 'business studio',
      billCycleDay: 0,
      currency: currency.value,
      thm_item_id__c: selectedAccommodationId.value,
      autoPay: true,
      hpmCreditCardPaymentMethodId: refId,
      billToContact: {
        address1: newBillingInformation.value?.address,
        city: newBillingInformation.value?.city,
        country: newBillingInformation.value?.country,
        firstName: newBillingInformation.value?.firstName,
        lastName: newBillingInformation.value?.lastName,
        state: newBillingInformation.value?.state ?? '',
        workEmail: newBillingInformation.value?.email,
        zipCode: newBillingInformation.value?.zipCode,
      },
      taxInfo: {
        exemptCertificateId: newBillingInformation.value?.taxId ?? '',
      },
      paymentMethodType: selectedPaymentMethodType.value,
    }

    await createNewZuoraAccount(
      { body, language: language.value },
      {
        onSuccess: data => {
          if (data?.data?.success) {
            createRateConnectSubscription()
            rcCampaignCreationInProgress.value = true
          } else {
            forceRender()
            displayNotification({
              message: 'subscriptions.details.transactionHistory.error',
              isTranslationKey: true,
              type: 'error',
            })
            sendErrorMetrics({
              itemId: selectedAccommodationId.value,
              productName: zuoraMetricConstants.productName.rateConnect,
              code: zuoraMetricConstants.metricErrorCodes.billingInformation,
              message: data?.data,
            })
            rcCampaignCreationInProgress.value = false
          }
        },
        onError: response => {
          displayNotification({
            message: 'subscriptions.details.transactionHistory.error',
            isTranslationKey: true,
            type: 'error',
          })
          sendErrorMetrics({
            itemId: selectedAccommodationId.value,
            productName: zuoraMetricConstants.productName.rateConnect,
            code: zuoraMetricConstants.metricErrorCodes.billingInformation,
            message: response.response.data?.message,
          })
          rcCampaignCreationInProgress.value = false
        },
      }
    )
  } else {
    if (!newBillingInformation.value) return
    const body = {
      billToContact: {},
      taxInfo: {},
    }

    if (refId) body.defaultPaymentMethodId = refId
    if (
      newBillingInformation.value.companyName &&
      newBillingInformation.value.companyName !== existingBillingInformation.value?.companyName
    )
      body.name = newBillingInformation.value.companyName
    if (
      newBillingInformation.value.address &&
      newBillingInformation.value.address !== existingBillingInformation.value?.address
    )
      body.billToContact.address1 = newBillingInformation.value.address
    if (newBillingInformation.value.city && newBillingInformation.value.city !== existingBillingInformation.value?.city)
      body.billToContact.city = newBillingInformation.value.city
    if (
      newBillingInformation.value.firstName &&
      newBillingInformation.value.firstName !== existingBillingInformation.value?.firstName
    )
      body.billToContact.firstName = newBillingInformation.value.firstName
    if (
      newBillingInformation.value.lastName &&
      newBillingInformation.value.lastName !== existingBillingInformation.value?.lastName
    )
      body.billToContact.lastName = newBillingInformation.value.lastName
    if (
      newBillingInformation.value.email &&
      newBillingInformation.value.email !== existingBillingInformation.value?.email
    )
      body.billToContact.workEmail = newBillingInformation.value.email

    if (
      newBillingInformation.value.state &&
      newBillingInformation.value.state !== existingBillingInformation.value?.state
    )
      body.billToContact.state = newBillingInformation.value.state

    body.billToContact.zipCode = newBillingInformation.value.zipCode ?? existingBillingInformation.value?.zipCode
    body.billToContact.country = newBillingInformation.value.country ?? existingBillingInformation.value?.country
    body.billToContact.state = countriesWithState.includes(body.billToContact.country)
      ? newBillingInformation.value.state
      : ''
    body.taxInfo.exemptCertificateId = newBillingInformation.value.taxId ?? existingBillingInformation.value?.taxId
    body.paymentMethodType = selectedPaymentMethodType.value

    if (Object.keys(body).length > 0) {
      await updateZuoraAccount(
        { body, language: language.value },
        {
          onSuccess: data => {
            if (data?.data?.success) {
              createRateConnectSubscription()
            } else {
              sendErrorMetrics({
                itemId: selectedAccommodationId.value,
                productName: zuoraMetricConstants.productName.rateConnect,
                code: zuoraMetricConstants.metricErrorCodes.billingInformation,
                message: data?.data,
              })
              forceRender()
              displayNotification({
                message: 'subscriptions.details.transactionHistory.error',
                isTranslationKey: true,
                type: 'error',
              })

              rcCampaignCreationInProgress.value = false
            }
          },
          onError: response => {
            sendErrorMetrics({
              itemId: selectedAccommodationId.value,
              productName: zuoraMetricConstants.productName.rateConnect,
              code: zuoraMetricConstants.metricErrorCodes.billingInformation,
              message: response.response.data?.message,
            })
            rcCampaignCreationInProgress.value = false
            forceRender()
            displayNotification({
              message: 'subscriptions.details.transactionHistory.error',
              isTranslationKey: true,
              type: 'error',
            })
          },
        }
      )
    }
  }
}

watch(defaultPaymentMethodId, newVal => {
  selectedPaymentMethod.value = newVal
})

onMounted(() => {
  currentStep.value = 2
  appAnalytics.track(appAnalytics.events.RC_PAYMENT_VISITED, {
    item_id: selectedAccommodationId.value,
  })
  if (defaultPaymentMethodId.value) {
    selectedPaymentMethod.value = defaultPaymentMethodId.value
  }
  if (isFromMarketingLink.value) {
    appAnalytics.track(appAnalytics.events.RC_VOUCHER_AUTO_APPLIED, {
      item_id: selectedAccommodationId.value,
      promoCode: appliedCode.value,
    })
  }
})

onBeforeUnmount(() => clearAllNotifications())
</script>
